import { useEffect, useState } from "react";
import { useI18N } from "@src/language/language-context";
import { getImmMeasurementUseCase } from "@src/cloud-connect/shot-data-table/use-case/get-imm-measurement-use-case";
import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import {
  ImmMeasurement,
  ShotTableColumn,
  ShotTableValue,
} from "@src/types/shot-data-table";

// Extracts the unit from the end of the ID when it is enclosed in square brackets,
// trims any surrounding whitespace, and returns an empty string if no unit is found.
// Example usage:
// Shotcounter [Shots] = Shots
// Act. inject time [s] = s
const getUnit = (id = "") => id.match(/\[([^\]]*)\]\s*$/)?.[1]?.trim() ?? "";

// Checks whether the provided value is a string that can be successfully parsed as a valid date/time.
// Returns true when the value represents a recognizable timestamp, otherwise returns false.
const isTimestampColumn = (value?: ShotTableValue) =>
  typeof value === "string" && !Number.isNaN(Date.parse(value));

export const useShotTableHook = () => {
  // Provides access to translated text used throughout the Shot Table.
  const i18n = useI18N();

  // Stores the index of the currently selected row.
  // null indicates that no row is currently selected.
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);

  // Stores the measurement data retrieved from the API.
  // null indicates that no measurement data is currently available.
  const [measurement, setMeasurement] = useState<ImmMeasurement | null>(null);

  // Tracks whether the measurement data is currently being fetched.
  const [isLoading, setIsLoading] = useState(true);

  // Provides translated text required by the Shot Table UI.
  const texts = {
    title: i18n("modules.shotTable.title"),
    noDataAvailable: i18n("modules.shotTable.noDataAvailable"),
    measurementLoadError: i18n("modules.shotTable.measurementLoadError"),
  };

  useEffect(() => {
    // Creates an AbortController so the API request can be cancelled
    // when the component using this hook is unmounted.
    const controller = new AbortController();
    const signal = controller.signal;

    // Fetches the IMM measurement data and updates the corresponding
    // loading, measurement states based on the API response.
    const loadMeasurement = async (signal: AbortSignal) => {
      setIsLoading(true);

      try {
        const result = await getImmMeasurementUseCase(signal);

        if (result.success) {
          setMeasurement(result.data as ImmMeasurement);
        } else {
          setMeasurement(null);
          errorSnackbar(texts.measurementLoadError);
        }
      } catch (error) {
        if (signal.aborted) return;

        console.error(error);
        setMeasurement(null);
        errorSnackbar(texts.measurementLoadError);
      } finally {
        if (!signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    // Start loading the measurement data when the hook is mounted.
    loadMeasurement(signal);

    // Cancel the in-flight request when the component is unmounted
    // to prevent unnecessary work and state updates after unmounting.
    return () => {
      controller.abort();
    };
  }, []);

  // Maps the API response headers to the column structure expected by the Shot Table.
  // The unit is extracted from the header ID when it is provided in square brackets.
  const columns: ShotTableColumn[] =
    measurement?.header?.map((header) => ({
      name: header.title ?? "",
      unit: getUnit(header.id),
    })) ?? [];

  // Uses the rows returned by the API, or an empty array when measurement data is unavailable.
  const rows = measurement?.rows ?? [];

  return {
    texts,
    columns,
    rows,
    selectedRowIndex,
    setSelectedRowIndex,
    isLoading,
    isTimestampColumn,
  };
};
